import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { AuthenticationContext } from '../../../shared/contexts/authentication.context';
import { CustomPowershellReport, Privilege } from '../../../shared/models/generated/ziroModels';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../shared/bottom-nav/bottom-nav.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { CustomPowershellQueriesContext } from '../../../shared/contexts/custom-powershell-queries.context';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { DateTimeFormat } from '../../../shared/filters/date-time-format.filter';
import { TranslateService } from '@ngx-translate/core';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { ToastService } from '../../../shared/services/toast.service';
import { tap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';

interface CustomPowershellReportRow extends DatatableRow {
  reportName: string;
  lastUpdated: string;
  id: number;
}

@Component({
  selector: 'ziro-custom-powershell-queries',
  templateUrl: './custom-powershell-queries.component.html',
  styleUrls: ['../../reporting.scss'],
  providers: [DateTimeFormat],
  standalone: false,
})
export class CustomPowershellQueriesComponent implements OnInit, OnDestroy {
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _authenticationContext = inject(AuthenticationContext);
  private _bottomNavService = inject(BottomNavService);
  private _customPowershellReportContext = inject(CustomPowershellQueriesContext);
  private _dateTimeFormat = inject(DateTimeFormat);
  private _translateService = inject(TranslateService);
  private _ziroModalService = inject(ZiroModalService);
  private _toastService = inject(ToastService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);

  ziroIcons = ZiroIcons;
  isLoading = true;
  isReadOnly = false;
  tenantId: string;
  columns: DatatableColumn<CustomPowershellReportRow>[] = [
    {
      name: 'reportName',
      columnSize: 'col-sm-6',
      label: 'tkey;reports.custom_reports.custom_report_list.report_name',
    },
    {
      name: 'lastUpdated',
      columnSize: 'col-sm-3',
      label: 'tkey;reports.custom_reports.custom_report_list.last_updated',
    },
  ];
  tableRows: CustomPowershellReportRow[] = [];

  private _reportsExporting: number[] = [];
  private _reports: CustomPowershellReport[];
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    ({ tenantId: this.tenantId } = this._route.snapshot.params);
    const sub = combineLatest([
      this._authenticationContext.state$,
      this._customPowershellReportContext.state$,
    ]).subscribe(([currentUser, customPowershellReports]) => {
      this._reports = customPowershellReports;

      this.isReadOnly = !currentUser.privileges.includes(Privilege.CONTROL_WRITE);
      if (!this.isReadOnly) {
        this._initBottomNav();
      }

      this._createTableRows();
      this.isLoading = false;
    });
    this._subscriptions.add(sub);

    this._breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;reports.microsoft.custom_powershell_reports.title' }]);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
  }

  private _initBottomNav(): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonsList([
        {
          id: 'custom-powershell-queries-add',
          dataAutomation: 'custom-powershell-queries-add',
          label: 'tkey;reports.microsoft.custom_powershell_reports.bottom_nav.add_report',
          icon: ZiroIcons.ADD,
          buttonClass: ButtonStyles.PRIMARY,
          type: ButtonTypes.LINK,
          href: window.location.href + '/new-report',
        },
      ]),
    );
  }

  private _createTableRows(): void {
    this.tableRows = this._reports.map((report) => {
      return {
        reportName: report.reportName,
        lastUpdated: this._dateTimeFormat.transform(report.lastUpdated),
        id: report.id,
        actions: [
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'report-delete',
            icon: ZiroIcons.DELETE_SMALL,
            isHidden: this.isReadOnly,
            disabled: this._reportsExporting.includes(report.id),
            disabledTooltip: 'tkey;reports.xlsx_edit_delete.button.disabled.tooltip',
            onClick: () => this._promptDeleteReport(report),
          },
          {
            style: ButtonStyles.OUTLINE_PRIMARY,
            type: ButtonTypes.LINK,
            href: window.location.href + `/${report.id}`,
            dataAutomation: 'report-edit',
            icon: ZiroIcons.EDIT,
            isHidden: this.isReadOnly,
            disabled: this._reportsExporting.includes(report.id),
            disabledTooltip: 'tkey;reports.xlsx_edit_delete.button.disabled.tooltip',
            onClick: () => this._editReport(report),
          },
          {
            type: ButtonTypes.BUTTON,
            style: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'report-export',
            icon: ZiroIcons.EXPORT,
            disabled: this._reportsExporting.includes(report.id),
            label: 'tkey;reports.xlsx_export.button',
            isPending: this._reportsExporting.includes(report.id),
            iconPending: ZiroIcons.SAVING,
            onClick: () => this._exportReport(report),
          },
        ],
        actionsColumnSize: 'col-sm-3',
      };
    });
  }

  private _exportReport(report: CustomPowershellReport): void {
    this._reportsExporting.push(report.id);
    this._createTableRows();
    this._customPowershellReportContext.export(report.id).subscribe(() => {
      this._reportsExporting = this._reportsExporting.filter((id: number) => id !== report.id);
      this._toastService.push(
        ToastTypes.SUCCESS,
        ZiroIcons.EXPORT,
        'tkey;reports.microsoft.custom_powershell_reports.toast.export.title',
        'tkey;reports.microsoft.custom_powershell_reports.toast.export.message',
      );
      this._createTableRows();
    });
  }

  private _editReport(report: CustomPowershellReport): void {
    this._router.navigate([report.id], { relativeTo: this._route });
  }

  private _promptDeleteReport(report: CustomPowershellReport): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        modalBodyIconHeaderClass: 'animated bounceIn lead text-center text-danger',
        title: this._translateService.instant('tkey;reports.microsoft.custom_powershell_reports.modal.delete.title'),
        promptBody: this._translateService.instant(
          'tkey;reports.microsoft.custom_powershell_reports.modal.delete.message',
          {
            reportName: report.reportName,
          },
        ),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => this._deleteReport(report),
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteReport(report: CustomPowershellReport): Observable<void> {
    this.isLoading = true;
    return this._customPowershellReportContext.delete(report.id).pipe(
      tap(() => {
        this._toastService.pushDeleteToast('tkey;reports.microsoft.custom_powershell_reports.title', report.reportName);
      }),
    );
  }
}
